import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eraser, Loader2, PenLine } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";

export function SignatureCanvas({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  const inked = useRef(false);
  const fit = () => {
    const c = ref.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    if (c.width === Math.round(rect.width * dpr) && c.height === Math.round(rect.height * dpr)) return;
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0f172a";
  };
  useEffect(() => { fit(); const t = setTimeout(fit, 250); return () => clearTimeout(t); }, []);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); if (!inked.current) fit(); ref.current.setPointerCapture(e.pointerId); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = ref.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p;
    inked.current = true;
    onChange(true);
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = ref.current;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    inked.current = false;
    onChange(false);
  };

  return (
    <div className="space-y-1.5">
      <canvas
        ref={ref}
        className="w-full h-44 rounded-md border-2 border-dashed border-slate-300 bg-white touch-none cursor-crosshair"
        onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} onPointerCancel={end}
        data-testid="signature-canvas"
      />
      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <span>Mit Finger, Stift oder Maus im Feld unterschreiben</span>
        <button type="button" onClick={clear} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="signature-clear-button"><Eraser className="h-3 w-3" />Löschen</button>
      </div>
    </div>
  );
}

export default function SignaturePadDialog({ open, onOpenChange, deliveryNote, onSigned }) {
  const [name, setName] = useState("");
  const [place, setPlace] = useState("");
  const [hasInk, setHasInk] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => { if (open) { setName(""); setPlace(""); setHasInk(false); } }, [open]);

  const save = async () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!hasInk || !canvas) { toast.error("Bitte zuerst unterschreiben"); return; }
    if (!name.trim()) { toast.error("Name der unterzeichnenden Person fehlt"); return; }
    setBusy(true);
    try {
      const r = await apiClient.post(`/delivery-notes/${deliveryNote.id}/signature`, { signature_png: canvas.toDataURL("image/png"), signer_name: name.trim(), place: place.trim() || null });
      toast.success(`Lieferschein ${deliveryNote.delivery_number} unterschrieben`);
      onSigned?.(r.data.signature);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unterschrift konnte nicht gespeichert werden");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenLine className="h-4 w-4" />Empfangsbestätigung {deliveryNote?.delivery_number}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-600">Der Kunde bestätigt mit seiner Unterschrift, die Ware vollständig und in einwandfreiem Zustand erhalten zu haben. Die Unterschrift wird im Lieferschein-PDF gedruckt.</p>
        <div ref={wrapRef}>{open && <SignatureCanvas onChange={setHasInk} />}</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="sig-name">Name (Empfänger/in)</Label>
            <Input id="sig-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Vorname Nachname" data-testid="signature-name-input" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="sig-place">Ort (optional)</Label>
            <Input id="sig-place" value={place} onChange={(e) => setPlace(e.target.value)} placeholder="z. B. Zürich" data-testid="signature-place-input" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="signature-cancel-button">Abbrechen</Button>
          <Button type="button" onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="signature-save-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PenLine className="h-4 w-4 mr-1" />}Unterschrift speichern
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
